import React, { useEffect, useState, useCallback } from "react";
import AppWrapper from "../AppWrapper";
import {
  Badge,
  Button,
  IndexTable,
  LegacyCard,
  Page,
  Spinner,
  Text,
  Modal,
  DatePicker,
  InlineError,
  Filters,
  ChoiceList,
  BlockStack,
  IndexFilters,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { useNavigate } from "react-router-dom";
import { formatFriendlyDate, formatPrice } from "../../utility/funcs";
import { UserAuth } from "../../context/AuthContext";

function InvoiceTracker() {
  const { hasPermission } = UserAuth();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // -----------------------------
  // Filters state
  // -----------------------------
  const [customerChoices, setCustomerChoices] = useState([]);
  const [customersToFilter, setCustomersToFilter] = useState([]);

  const [projectChoices, setProjectChoices] = useState([]);
  const [projectsToFilter, setProjectsToFilter] = useState([]);

  const [sortSelected, setSortSelected] = useState(["created_at desc"]);

  const [portalToFilter, setPortalToFilter] = useState([]);
  const portalChoices = [
    { label: "Pendiente", value: "pending" },
    { label: "Cargada", value: "set" },
  ];

  const [statusToFilter, setStatusToFilter] = useState([]);
  const statusChoices = [
    { label: "Prefactura", value: "draft" },
    { label: "Por pagar", value: "open" },
  ];

  // -----------------------------
  // Modal state
  // -----------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [{ month, year }, setMonthYear] = useState(() => {
    const today = new Date();
    return { month: today.getMonth(), year: today.getFullYear() };
  });

  // -----------------------------
  // Fetch invoices
  // -----------------------------
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);

      const { data, error } = await supabase
        .from("tracked_invoices")
        .select(
          `
          id,
          invoice_number,
          created_at,
          due_date,
          status,
          client_name,
          project,
          total,
          balance,
          portal_uploaded_at
        `,
        )
        .like("project", "ICA%")
        .gt("balance", 0)
        .order("due_date", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) {
        console.error(error);
        setLoading(false);
        return;
      }

      if (invoices.length === 0) setInvoices(data);

      // Build filter choices
      const customers = [
        ...new Set(data.map((i) => i.client_name).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, "es"));

      const projects = [
        ...new Set(data.map((i) => i.project).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, "es"));

      setCustomerChoices(customers.map((c) => ({ label: c, value: c })));
      setProjectChoices(projects.map((p) => ({ label: p, value: p })));

      setLoading(false);
    };
    if (hasPermission("invoice.tracker.view")) fetchInvoices();
  }, [hasPermission]);

  // -----------------------------
  // Filtering logic
  // -----------------------------
  let filteredInvoices = invoices;

  if (customersToFilter.length > 0) {
    filteredInvoices = filteredInvoices.filter((i) =>
      customersToFilter.includes(i.client_name),
    );
  }

  if (projectsToFilter.length > 0) {
    filteredInvoices = filteredInvoices.filter((i) =>
      projectsToFilter.includes(i.project),
    );
  }

  if (portalToFilter.length > 0) {
    filteredInvoices = filteredInvoices.filter((i) =>
      portalToFilter.includes(i.portal_uploaded_at ? "set" : "pending"),
    );
  }

  if (statusToFilter.length > 0) {
    filteredInvoices = filteredInvoices.filter((i) =>
      statusToFilter.includes(i.status),
    );
  }

  const sortedInvoices = (() => {
    if (!sortSelected || sortSelected.length === 0) return filteredInvoices;

    const [field, direction] = sortSelected[0].split(" ");

    return [...filteredInvoices].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      // Handle nulls (always last)
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Dates
      if (field.includes("date") || field.includes("at")) {
        const aTime = new Date(aValue).getTime();
        const bTime = new Date(bValue).getTime();
        return direction === "asc" ? aTime - bTime : bTime - aTime;
      }

      // Numbers
      if (typeof aValue === "number") {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Strings
      return direction === "asc"
        ? String(aValue).localeCompare(String(bValue), "es")
        : String(bValue).localeCompare(String(aValue), "es");
    });
  })();

  // -----------------------------
  // Filters config
  // -----------------------------
  const filters = [
    {
      key: "customer",
      label: "Cliente",
      filter: (
        <ChoiceList
          title="Cliente"
          titleHidden
          choices={customerChoices}
          selected={customersToFilter}
          onChange={setCustomersToFilter}
          allowMultiple
        />
      ),
      pinned: true,
    },
    {
      key: "project",
      label: "Proyecto",
      filter: (
        <ChoiceList
          title="Proyecto"
          titleHidden
          choices={projectChoices}
          selected={projectsToFilter}
          onChange={setProjectsToFilter}
          allowMultiple
        />
      ),
      pinned: true,
    },
    {
      key: "portal",
      label: "Fecha de Portal",
      filter: (
        <ChoiceList
          title="Portal"
          titleHidden
          choices={portalChoices}
          selected={portalToFilter}
          onChange={setPortalToFilter}
          allowMultiple
        />
      ),
      pinned: true,
    },
    {
      key: "status",
      label: "Status",
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={statusChoices}
          selected={statusToFilter}
          onChange={setStatusToFilter}
          allowMultiple
        />
      ),
      pinned: true,
    },
  ];

  const appliedFilters = [];

  if (customersToFilter.length)
    appliedFilters.push({
      key: "customer",
      label: `Cliente: ${customersToFilter.join(", ")}`,
      onRemove: () => setCustomersToFilter([]),
    });

  if (projectsToFilter.length)
    appliedFilters.push({
      key: "project",
      label: `Proyecto: ${projectsToFilter.join(", ")}`,
      onRemove: () => setProjectsToFilter([]),
    });

  if (portalToFilter.length)
    appliedFilters.push({
      key: "portal",
      label: `Portal: ${portalToFilter.join(", ")}`,
      onRemove: () => setPortalToFilter([]),
    });

  if (statusToFilter.length)
    appliedFilters.push({
      key: "status",
      label: `Status: ${statusToFilter.join(", ")}`,
      onRemove: () => setStatusToFilter([]),
    });

  const handleClearAll = useCallback(() => {
    setCustomersToFilter([]);
    setProjectsToFilter([]);
    setPortalToFilter([]);
    setStatusToFilter([]);
  }, []);

  // -----------------------------
  // Modal handlers
  // -----------------------------
  const openModal = (invoice) => {
    const today = new Date();
    setSelectedInvoice(invoice);
    setSelectedDate(today);
    setMonthYear({ month: today.getMonth(), year: today.getFullYear() });
    setError(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedInvoice) return;

    setSaving(true);
    const isoDate = selectedDate.toISOString();

    setInvoices((prev) =>
      prev.map((i) =>
        i.id === selectedInvoice.id ? { ...i, portal_uploaded_at: isoDate } : i,
      ),
    );

    const { error } = await supabase
      .from("tracked_invoices")
      .update({ portal_uploaded_at: isoDate })
      .eq("id", selectedInvoice.id);

    if (error) {
      console.error(error);
      setError("No se pudo guardar la fecha");
      setSaving(false);
      return;
    }

    setSaving(false);
    setModalOpen(false);
  };

  // -----------------------------
  // Helpers
  // -----------------------------
  const daysSince = (invoice) => {
    const base = invoice.portal_uploaded_at || invoice.created_at;
    const start = new Date(base);
    const today = new Date();
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.floor((today - start) / (1000 * 60 * 60 * 24));
  };

  const statusBadge = (status) => {
    switch (status) {
      case "draft":
        return <Badge tone="info">Prefactura</Badge>;
      case "open":
        return <Badge tone="attention">Por pagar</Badge>;
      case "closed":
        return <Badge tone="success">Pagada</Badge>;
      case "void":
        return <Badge>Cancelada</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // -----------------------------
  // Rows
  // -----------------------------
  const rowMarkup = sortedInvoices.map((invoice, index) => (
    <IndexTable.Row id={invoice.id} key={invoice.id} position={index}>
      <IndexTable.Cell>
        <Text tone="subdued">{invoice.invoice_number}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{invoice.client_name}</IndexTable.Cell>
      <IndexTable.Cell>{invoice.project ?? "-"}</IndexTable.Cell>
      <IndexTable.Cell>
        {formatFriendlyDate(invoice.created_at)}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {invoice.portal_uploaded_at ? (
          formatFriendlyDate(invoice.portal_uploaded_at)
        ) : (
          <span
            onClick={() => {
              if (!hasPermission("invoice.tracker.update")) return;
              openModal(invoice);
            }}
            style={{
              cursor: hasPermission("invoice.tracker.update")
                ? "pointer"
                : "default",
            }}
          >
            <Badge tone="attention">Pendiente</Badge>
          </span>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>{daysSince(invoice)} días</IndexTable.Cell>
      <IndexTable.Cell>{formatPrice(invoice.total)}</IndexTable.Cell>
      <IndexTable.Cell>{formatPrice(invoice.balance)}</IndexTable.Cell>
      <IndexTable.Cell>{statusBadge(invoice.status)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  const sortOptions = [
    {
      label: "Fecha de creación",
      value: "created_at asc",
      directionLabel: "Más antiguas",
    },
    {
      label: "Fecha de creación",
      value: "created_at desc",
      directionLabel: "Más recientes",
    },
    {
      label: "Fecha de portal",
      value: "portal_uploaded_at asc",
      directionLabel: "Más antiguas",
    },
    {
      label: "Fecha de portal",
      value: "portal_uploaded_at desc",
      directionLabel: "Más recientes",
    },
    {
      label: "Cliente",
      value: "client_name asc",
      directionLabel: "A–Z",
    },
    {
      label: "Cliente",
      value: "client_name desc",
      directionLabel: "Z–A",
    },
    {
      label: "Saldo",
      value: "balance asc",
      directionLabel: "Menor a mayor",
    },
    {
      label: "Saldo",
      value: "balance desc",
      directionLabel: "Mayor a menor",
    },
  ];

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <AppWrapper>
      <Page
        fullWidth
        title="Facturas en Proceso de Cobro"
        subtitle="Facturas con procesos pendientes antes de poder cobrar"
        backAction={{ onAction: () => navigate(-1) }}
      >
        {loading ? (
          <Spinner size="small" />
        ) : (
          <div style={{ marginBottom: "1rem" }}>
            <BlockStack gap="200">
              <Filters
                filters={filters}
                appliedFilters={appliedFilters}
                onClearAll={handleClearAll}
                hideQueryField
              />
              <LegacyCard>
                <IndexTable
                  resourceName={{ singular: "Factura", plural: "Facturas" }}
                  selectable={false}
                  itemCount={sortedInvoices.length}
                  headings={[
                    { title: "Folio" },
                    { title: "Cliente" },
                    { title: "Proyecto" },
                    { title: "Fecha de Creación" },
                    { title: "Fecha de Portal" },
                    { title: "Días" },
                    { title: "Total" },
                    { title: "Saldo" },
                    { title: "Status" },
                  ]}
                >
                  {rowMarkup}
                </IndexTable>
              </LegacyCard>
            </BlockStack>
          </div>
        )}

        {/* Modal */}
        <Modal
          open={modalOpen}
          onClose={() => !saving && setModalOpen(false)}
          title={`${selectedInvoice?.invoice_number} - Fecha de carga en portal`}
          primaryAction={{
            content: saving ? "Guardando..." : "Guardar",
            onAction: handleSave,
            disabled: saving,
          }}
          secondaryActions={[
            {
              content: "Cancelar",
              onAction: () => setModalOpen(false),
              disabled: saving,
            },
          ]}
        >
          <Modal.Section>
            <DatePicker
              month={month}
              year={year}
              onMonthChange={(m, y) => setMonthYear({ month: m, year: y })}
              onChange={({ start }) => setSelectedDate(start)}
              selected={selectedDate}
              disableDatesAfter={new Date()}
            />
            {error && <InlineError message={error} />}
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default InvoiceTracker;
